import { useBreakpoints } from 'common/hooks';
import { getIsFallback } from 'features/products/ducks/selectors';
import { SponsoredProductType } from 'features/products/types';
import { Product } from 'common/components/product-card/types';
import { useSelector } from 'react-redux';
import { SponsoredCarouselProps } from './types';
import { detectDeviceFromUserAgent, getLimitedSponsoredProducts } from './utils';
import { RootState } from 'typesafe-actions';

export const useSponsoredProducts = (
  fetchedProducts: Product[] | SponsoredProductType[] | null,
  sponsoredProps?: SponsoredCarouselProps,
) => {
  const isResponseFallback = useSelector(getIsFallback);
  const breakpoints = useBreakpoints();

  const userAgentFromState = useSelector(({ userAgent }: RootState) => userAgent);

  const deviceType = () => {
    if (typeof window !== 'undefined') {
      return {
        isMobile: breakpoints?.smMobile || breakpoints?.mobile,
        isDesktop: breakpoints?.tablet || breakpoints?.desktop || breakpoints?.largeDesktop,
      };
    }

    return detectDeviceFromUserAgent(userAgentFromState);
  };

  const { isMobile, isDesktop } = deviceType();

  const shouldShowSponsored = (props?: SponsoredCarouselProps): props is SponsoredCarouselProps =>
    !!props && !!props.useFallback && !isFallback;

  if (!sponsoredProps) {
    return {
      products: fetchedProducts,
      shouldShowSponsored,
    };
  }

  const { limitMinMobile, limitMaxMobile, useFallback } = sponsoredProps;
  const shouldLimitProducts = limitMinMobile || limitMaxMobile;

  const sponsoredProducts =
    (fetchedProducts as SponsoredProductType[] | null)?.filter((product) => product.monetization) ?? [];

  const limitedSponsoredProducts = shouldLimitProducts
    ? getLimitedSponsoredProducts(sponsoredProducts, { isMobile, isDesktop }, sponsoredProps)
    : sponsoredProducts;

  const fallbackProducts =
    fetchedProducts?.filter((product) => !(product as SponsoredProductType).monetization) ?? [];

  const isFallback = isResponseFallback || !limitedSponsoredProducts?.length;

  const productsWithFallback = isFallback ? fallbackProducts : limitedSponsoredProducts;

  const productsWithoutFallback = [...limitedSponsoredProducts, ...fallbackProducts];

  const products = useFallback ? productsWithFallback : productsWithoutFallback;

  return {
    products,
    shouldShowSponsored,
  };
};
